/**
 * Import 미리보기용 기존 TOTP 지문 전수 수집 (PR7 #1514 — 인터뷰 확정 B안)
 *
 * 제로지식이라 서버는 중복을 판정할 수 없다 — 내 TOTP 레코드 전체를 조회(PR3
 * include_totp API)해 브라우저에서 복호한 뒤 SHA-256 지문 집합을 만든다.
 * import 는 단발 이벤트라 일회성 복호 비용(수십 건 RSA unwrap)을 수용(설계 §5).
 * 복호한 지문은 PR8 세션 registry 에도 등록해 허브 배지 커버리지를 함께 넓힌다.
 * 복호 실패 레코드는 지문을 만들 수 없어 제외하되 failedCount 로 고지한다(Gemini 수용).
 */
import ResourceService from 'api/Resource'
import { getRsKeyObjFromResource } from 'pages/records/components/RecordCardContent'
import { totpFingerprint, register } from 'services/totp/duplicateRegistry'
import { createSemaphore } from 'utils/importExport/bulkExport'

const PAGE_LIMIT = 100 // /v1/resources 페이지 상한(PR3 실측)

export async function fetchExistingTotpFingerprints() {
  // 1) 페이지 순회로 대상 레코드 전부 수집(순차 — offset 의존)
  const allItems = []
  let offset = 0
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const response = await ResourceService.getAll({
      has_totp: 'true',
      include_totp: 'true',
      limit: PAGE_LIMIT,
      offset,
    })
    const items = response?.data || []
    allItems.push(...items)
    if (items.length < PAGE_LIMIT) break
    offset += PAGE_LIMIT
  }

  // 2) 일괄 복호 → 지문 집합 (실패는 건수만 집계 — 사유/암호문 로그 금지).
  // resource_key 가 없어도 group_key 로 복호 가능(getRsKeyObjFromResource 계약 —
  // 리뷰 C2: 그룹 공유 TOTP 누락 방지). totp_field 자체가 없으면 서버가 재료를
  // 안 준 것(만료 등)이라 확인 불가로 집계.
  // 동시 복호는 3 으로 제한(리뷰 D1 — bulkImport 의 세마포어 선례, RSA unwrap 폭주 방지).
  const semaphore = createSemaphore(3)
  const results = await Promise.all(
    allItems.map(async (record) => {
      if (record?.totp_field == null) return null
      await semaphore.acquire()
      try {
        const rsKeyObj = await getRsKeyObjFromResource(record)
        const plain = await rsKeyObj.decrypt(record.totp_field)
        const fp = totpFingerprint(plain)
        if (fp == null) return null
        register(record.id, fp, record.title) // PR8 registry 겸용 등록
        return fp
      } catch (e) {
        return null
      } finally {
        semaphore.release()
      }
    }),
  )

  const fingerprints = new Set(results.filter(Boolean))
  const failedCount = results.filter((fp) => fp == null).length

  return { fingerprints, failedCount }
}

export default fetchExistingTotpFingerprints
